<template>
  <div>
    <CRow>
      <CCol col="12" xl="12">
        <CButtonToolbar>
          <CButton 
            color="ghost-success" 
            shape="pill"
            @click="showSectionEditor=true"
          >
            <CIcon size="lg" name="cil-pen"/>
          </CButton>
          <CButton
            color="ghost-success" 
            shape="pill"
            @click="showPageDevTool=true"
          >
            <CIcon size="lg" name="cil-code"/>
          </CButton>
          <CButton
            color="ghost-success" 
            shape="pill"
            @click="showSectionPreview=true"
          >
            <CIcon size="lg" name="cil-find-in-page"/>
          </CButton>
        </CButtonToolbar>
      </CCol>
      <CCol col="12" lg="12">
        <CCard>
          <CCardBody>
            <AppForm 
              :key="appFormKey"
              :vo-name="isEdit ? 'SectionBuilderGetDraftVo' : 'SectionBuilderAddDraftVo'"
              :data="tmpSectionBuilder" 
              @changed="onFormChange"
            />
          </CCardBody>
          <CCardFooter>
            <div class="btn-group" role="group" aria-label="Button group with nested dropdown">
              <div class="btn-group" role="group">
                <button 
                  id="btnGroupDrop1" 
                  type="button" 
                  class="btn btn-primary dropdown-toggle" 
                  data-toggle="dropdown" 
                  aria-haspopup="true" 
                  aria-expanded="false"
                  :disabled="updateSectionBuilderLoading"
                  >
                  <span v-on:click.self="() => onAddOrUpdateDraftSectionBuilder(false)" >{{i18nMessage.SaveDraft}}</span>
                </button>
                <div class="dropdown-menu dropdown-menu-left" aria-labelledby="btnGroupDrop1">
                  <LoadingStateButton
                    @click="() => onAddOrUpdateDraftSectionBuilder(true)"
                    :text="i18nMessage.Deploy"
                    color="success"
                    :loading="updateSectionBuilderLoading"
                    class="btn-block"
                  />
                  <template v-if="isEdit">
                    <LoadingStateButton
                      @click="onDiscardDraftSectionBuilder"
                      :text="i18nMessage.DiscardChange"
                      color="danger"
                      :loading="updateSectionBuilderLoading"
                      class="btn-block"
                    />
                  </template>
                </div>
              </div>
            </div>
            <template v-if="isEdit">
              &nbsp;
              <CButton color="secondary" @click="showDeleteModal = true">{{i18nMessage.Delete}}</CButton>
            </template>
            &nbsp;
            <CButton color="secondary" @click="goBack">{{i18nMessage.Back}}</CButton>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
    <CModal
      :addContentClasses="'overlay bg-white section-modal'"
      :fade="false"
      id="section-preview-modal"
      title="Section Preview"
      :show.sync="showSectionPreview"
    >
      <PageRender
        v-if="showSectionPreview"
        :id="tmpSectionBuilder.id"
        :sections="[tmpSectionBuilder,]"
        :global-style="this.defaultGlobalStyles"
      />
    </CModal>
    <CModal
      :addContentClasses="'overlay bg-white section-modal'"
      :fade="false"
      id="page-dev-tool-modal"
      title="Page Dev Tool"
      :show.sync="showPageDevTool"
    >
      <PageDevTool
        v-if="showPageDevTool"
        :value="tmpSectionBuilder"
        @input="onPageDevToolContentChange"
        :showCodeEditors="true"
      />
    </CModal>
    <CModal
      :addContentClasses="'overlay bg-white section-modal'"
      :fade="false"
      id="section-editor-modal"
      title="Section Editor"
      :show.sync="showSectionEditor"
    >
      <HtmlEditor
        id="section-editor"
        v-if="showSectionEditor"
        :global-styles="this.defaultGlobalStyles"
        :content="(tmpSectionBuilder.template ? tmpSectionBuilder.template  : ' ') + '<style>' + (tmpSectionBuilder.style ? tmpSectionBuilder.style : '') + '</style>'"
        @change="onEditorContentChange"
      />
    </CModal>
    <CModal
        :show.sync="showDeleteModal"
        :centered="true"
        color="default"
      >
        {{i18nMessage.PleaseInputTextToConfirmDelete}} <b>{{sectionBuilder.name}}</b>
        <br/><br/>
        <CInput
            :placeholder="i18nMessage.PleaseInputText"
            v-model="deleteConfirmText"
          />
        <template #header>
          <span class="modal-title">{{i18nMessage.Delete}}？</span>
          <CButtonClose @click="showDeleteModal = false" class="text-dark"/>
        </template>
        <template #footer>
          <LoadingStateButton
            :disabled="deleteConfirmText !== sectionBuilder.name"
            @click="onDeleteSectionBuilder"
            :text="i18nMessage.Apply"
            color="danger"
            :loading="deleteSectionBuilderLoading"
          />
          <CButton @click="showDeleteModal = false" color="secondary">{{i18nMessage.Cancel}}</CButton>
        </template>
      </CModal>
  </div>
</template>

<style>
  .section-modal.overlay {
    position: fixed;
    width: 100%;
    height: 100vh;
    left: 0;
    top: 0;
    background: rgba(51,51,51,0.7);
    z-index: 10;
  }

  .section-modal > .modal-header {
    padding: 5px;
  }

  .section-modal > .modal-content {
    overflow: hidden;
  }

  .section-modal > .modal-body {
    overflow-y: scroll;
    padding: 0px;
  }

  .section-modal > .modal-footer {
    display: none;
  }
</style>

<script>
import { mapState, mapActions, mapMutations, } from 'vuex'
import { generateGUID, isEmpty } from '../../util'
import AppForm from '../../components/AppForm'
import PageRender from '../../page-lib/components/PageRender'
import LoadingStateButton from '../../components/LoadingStateButton'
import HtmlEditor from '../../components/HtmlEditor'
import PageDevTool from '../../components/PageDevTool'


export default {
  name: 'SectionBuilder',
  components: {
    AppForm,
    LoadingStateButton,
    PageRender,
    HtmlEditor,
    PageDevTool,
  },
  data () {
    return {
      deleteConfirmText: null,
      showDeleteModal: false,
      isEdit: this.$route.params.id && this.$route.params.id.length > 0,
      tmpSectionBuilder: {},
      updateSectionBuilderLoading: false,
      deleteSectionBuilderLoading: false,
      appFormKey: generateGUID(),
      showSectionPreview: false,
      showSectionEditor: false,
      showPageDevTool: false,
    }
  },
  computed: {
    ...mapState({
      i18nMessage: state => state.i18nMessage,
      sectionBuilder: state => state.pageBuilder.sectionBuilder,
      defaultGlobalStyles: state => state.defaultGlobalStyles,
    }),
  },
  methods: {
    ...mapActions({
      getDraftSectionBuilder: 'pageBuilder/getDraftSectionBuilder',
      addDraftSectionBuilder: 'pageBuilder/addDraftSectionBuilder',
      updateDraftSectionBuilder: 'pageBuilder/updateDraftSectionBuilder',
      deployDraftSectionBuilder: 'pageBuilder/deployDraftSectionBuilder',
      discardDraftSectionBuilder: 'pageBuilder/discardDraftSectionBuilder',
      deleteSectionBuilder: 'pageBuilder/deleteSectionBuilder',
    }),
    ...mapMutations({
      setSectionBuilder: 'pageBuilder/setSectionBuilder',
    }),
    goBack() {
      this.$router.push({path: '/section-builder'})
    },
    onEditorContentChange (template, style) {
      this.tmpSectionBuilder = {
        ...this.tmpSectionBuilder,
        template,
        style,
      }
    },
    onPageDevToolContentChange (data) {
      this.tmpSectionBuilder = {...data}
    },
    onFormChange (data) {
      this.tmpSectionBuilder = {...data}
    },
    onDeleteSectionBuilder () {
      this.deleteSectionBuilderLoading = true
      this.deleteSectionBuilder(this.sectionBuilder.id)
          .then(() => {
            this.deleteSectionBuilderLoading = false
            this.appFormKey = generateGUID()
            this.goBack()
          }, () => {
            this.deleteSectionBuilderLoading = false
            this.showDeleteModal = false
          })
    },
    onAddOrUpdateDraftSectionBuilder (isDeploy) {
      if (isEmpty(this.tmpSectionBuilder)) {
        return
      }
      if (this.tmpSectionBuilder.id) {
        this.updateSectionBuilderLoading = true
        this.updateDraftSectionBuilder({
          input: this.tmpSectionBuilder,
          isDeploy: isDeploy,
        })
          .then(() => {
            this.updateSectionBuilderLoading = false
            this.appFormKey = generateGUID()
            this.goBack()
          }, () => {
            this.updateSectionBuilderLoading = false
          })
      } else {
        this.updateSectionBuilderLoading = true
        this.addDraftSectionBuilder({
          input: this.tmpSectionBuilder,
          isDeploy: isDeploy,
        })
          .then(() => {
            this.updateSectionBuilderLoading = false
            this.appFormKey = generateGUID()
            this.goBack()
          }, () => {
            this.updateSectionBuilderLoading = false
          })
      }
    },
    onDiscardDraftSectionBuilder () {
      if (this.tmpSectionBuilder.id) {
        this.updateSectionBuilderLoading = true
        this.discardDraftSectionBuilder(this.tmpSectionBuilder.id)
          .then(() => {
            this.updateSectionBuilderLoading = false
            this.appFormKey = generateGUID()
            this.goBack()
          }, () => {
            this.updateSectionBuilderLoading = false
          })
      }
    },
  },
  mounted() {
    if (this.isEdit) {
      this.getDraftSectionBuilder(this.$route.params.id).then(() => {
        this.tmpSectionBuilder = this.sectionBuilder
      })
    }
  },
  destroyed() {
    this.setSectionBuilder({})
  },
  watch: {},
}
</script>
